import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { Icons } from "../../constants/icon-library";
import {
  editTool,
  getToolById,
  resetToolState,
  addTool,
  removeTool,
} from "../../features/tools/toolsSlice";
import {
  buildRequiredList,
  copyToClipboard,
  createKeyValue,
  createProperty,
  newId,
  toolInitialValues,
  toolValidationSchema,
  toSnakeCase,
  parseCurlCommand,
} from "../../constants/tool.constants";
import { confirmationModal } from "../../constants/confirmation.constants";
import { KeyValueEditor, SectionCard } from "./KeyValueEditor";

const ToolEditor = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { uuid, toolId } = useParams();
  const toolUuidRef = useRef(null);
  const [lastCopiedPlaceholder, setLastCopiedPlaceholder] = useState(null);
  const [showCurlInput, setShowCurlInput] = useState(false);
  const [curlInput, setCurlInput] = useState("");
  const isEditMode = Boolean(toolId);
  const toolState = useSelector((state) => state?.tools);
  const currentTool = toolState?.currentTool;
  const loading = toolState?.loading ?? false;
  const success = toolState?.success ?? false;
  const { onAssistantUpdated } = useOutletContext() || {};

  const editorFocusStyle = `
    textarea:focus {
      background-color: #2d3748 !important;
      color: #e2e8f0 !important;
      border-color: #4299e1 !important;
      box-shadow: none !important;
      outline: none !important;
    }
  `;

  const formik = useFormik({
    initialValues: toolInitialValues || {},
    validationSchema: toolValidationSchema,
    enableReinitialize: true,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: handleSubmit,
  });

  const { values, setValues, errors, touched, isSubmitting, submitCount } =
    formik;

  async function handleSubmit(formValues) {
    try {
      let result = null;

      if (toolUuidRef?.current) {
        result = await dispatch(
          editTool({
            uuid: toolUuidRef.current,
            data: formValues || {},
          })
        ).unwrap();
      } else {
        result = await dispatch(
          addTool({
            assistantId: uuid || null,
            toolData: { ...(formValues || {}) },
          })
        ).unwrap();
      }

      if (!result?.success) {
        toast.error("Something went wrong!");
        return;
      }

      toast.success(
        toolUuidRef?.current
          ? "Tool updated successfully"
          : "Tool saved successfully"
      );
      navigate(`/my-assistants/${uuid}`, {
        state: { activeTab: "tools" },
      });
      if (onAssistantUpdated) {
        onAssistantUpdated();
      }
    } catch (error) {
      console.error(error);
      toast.error("Something went wrong");
    }
  }

  const handleDelete = async () => {
    if (!toolUuidRef?.current) {
      toast.error("No Tool to delete");
      return;
    }

    const result = await confirmationModal({
      title: "Are you sure?",
      text: "This will permanently delete the tool.",
    });

    if (result?.isConfirmed) {
      try {
        await dispatch(removeTool(toolUuidRef.current)).unwrap();
        toast.success("Tool deleted successfully");
        toolUuidRef.current = null;
        formik.resetForm();
        navigate(`/my-assistants/${uuid}`, {
          state: { activeTab: "tools" },
        });
        if (onAssistantUpdated) {
          onAssistantUpdated();
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to delete tool");
      }
    }
  };

  const ensureIds = (list) => {
    if (!Array.isArray(list) || list.length === 0) return [createKeyValue()];
    return list.map((item) => ({
      ...item,
      id: item.id || newId(),
    }));
  };

  useEffect(() => {
    if (toolId) {
      dispatch(getToolById(toolId))
        .then((res) => {
          const toolData = res?.payload?.data;
          if (toolData) {
            toolUuidRef.current = toolData?.uuid || toolId;
            const normalizedProperties = Array.isArray(
              toolData?.parameters?.properties
            )
              ? toolData.parameters.properties.map((property) => ({
                  id: property?.id || newId(),
                  key: toSnakeCase(property?.key || ""),
                  type: property?.type || "String",
                  description: property?.description || "",
                  isRequired: Boolean(property?.isRequired),
                }))
              : [];
            const formValues = {
              name: toolData?.name || "",
              toolSlug: toolData?.toolSlug || "",
              description: toolData?.description || "",
              parameters: {
                properties: normalizedProperties,
                required: buildRequiredList(normalizedProperties),
              },
              executionLogic: {
                type: toolData?.executionLogic?.type || "API_CALL",
                details: {
                  url: toolData?.executionLogic?.details?.url || "",
                  method: toolData?.executionLogic?.details?.method || "POST",
                  headers: ensureIds(
                    toolData?.executionLogic?.details?.headers
                  ),
                  queryParams: ensureIds(
                    toolData?.executionLogic?.details?.queryParams
                  ),
                  body: toolData?.executionLogic?.details?.body,
                  authType:
                    toolData?.executionLogic?.details?.authType || "NONE",
                },
              },
            };
            setValues(formValues);
          }
        })
        .catch((error) => {
          console.error("Failed to fetch tool:", error);
          toast.error("Failed to load tool details");
        });
    } else {
      toolUuidRef.current = null;
      setValues(toolInitialValues || {});
    }
  }, [toolId, dispatch, setValues]);

  useEffect(() => {
    if (success) {
      navigate(-1);
      dispatch(resetToolState());
    }
  }, [success, navigate, dispatch]);

  const handleAddProperty = () => {
    const currentProperties = Array.isArray(values?.parameters?.properties)
      ? values.parameters.properties
      : [];
    const updatedProperties = [...currentProperties, createProperty()];
    setValues({
      ...values,
      parameters: {
        ...values?.parameters,
        properties: updatedProperties,
        required: buildRequiredList(updatedProperties),
      },
    });
  };

  const handleUpdateProperty = (id, field, value) => {
    const currentProperties = Array.isArray(values?.parameters?.properties)
      ? values.parameters.properties
      : [];
    const updatedProperties = currentProperties.map((item) => {
      if (item?.id !== id) return item;
      if (field === "isRequired") {
        return { ...item, [field]: Boolean(value) };
      }
      if (field === "key") {
        return { ...item, [field]: toSnakeCase(value) };
      }
      return { ...item, [field]: value ?? "" };
    });
    setValues({
      ...values,
      parameters: {
        ...values?.parameters,
        properties: updatedProperties,
        required: buildRequiredList(updatedProperties),
      },
    });
  };

  const handleRemoveProperty = (id) => {
    const currentProperties = Array.isArray(values?.parameters?.properties)
      ? values.parameters.properties
      : [];
    setValues({
      ...values,
      parameters: {
        ...values?.parameters,
        properties: currentProperties.filter((item) => item?.id !== id),
        required: buildRequiredList(
          currentProperties.filter((item) => item?.id !== id)
        ),
      },
    });
  };

  const handleUpdateKeyValue = (setter, id, field, value) => {
    if (setter === "executionLogic") {
      const currentHeaders = Array.isArray(
        values?.executionLogic?.details?.headers
      )
        ? values.executionLogic.details.headers
        : [];
      const currentParams = Array.isArray(
        values?.executionLogic?.details?.queryParams
      )
        ? values.executionLogic.details.queryParams
        : [];

      const updatedHeaders = currentHeaders.map((item) =>
        item?.id === id ? { ...item, [field]: value } : item
      );
      const updatedParams = currentParams.map((item) =>
        item?.id === id ? { ...item, [field]: value } : item
      );

      setValues({
        ...values,
        executionLogic: {
          ...values.executionLogic,
          details: {
            ...values.executionLogic.details,
            headers: updatedHeaders,
            queryParams: updatedParams,
          },
        },
      });
    }
  };

  const handleAddKeyValue = (setter) => {
    if (setter === "executionLogic") {
      const activeTab = values?._activeTab || "params";
      const fieldName = activeTab === "params" ? "queryParams" : "headers";

      const currentArray = Array.isArray(
        values?.executionLogic?.details?.[fieldName]
      )
        ? values.executionLogic.details[fieldName]
        : [];

      setValues({
        ...values,
        executionLogic: {
          ...values.executionLogic,
          details: {
            ...values.executionLogic.details,
            [fieldName]: [...currentArray, createKeyValue()],
          },
        },
      });
    }
  };

  const handleRemoveKeyValue = (setter, id) => {
    if (setter === "executionLogic") {
      const activeTab = values?._activeTab || "params";
      const fieldName = activeTab === "params" ? "queryParams" : "headers";

      const currentArray = Array.isArray(
        values?.executionLogic?.details?.[fieldName]
      )
        ? values.executionLogic.details[fieldName]
        : [];

      if (currentArray.length === 0) return;

      const filtered = currentArray.filter((item) => item?.id !== id);

      setValues({
        ...values,
        executionLogic: {
          ...values.executionLogic,
          details: {
            ...values.executionLogic.details,
            [fieldName]: filtered.length ? filtered : [createKeyValue()],
          },
        },
      });
    }
  };

  const executionLogic = values?.executionLogic;
  const details = executionLogic?.details || {};
  const activeTab = values?._activeTab || "params";
  const parameterPlaceholders = [
    {
      id: "access_token",
      label: "access_token",
      token: "{{access_token}}",
    },
    {
      id: "id_1",
      label: "id_1",
      token: "{{id_1}}",
    },
    {
      id: "id_2",
      label: "id_2",
      token: "{{id_2}}",
    },
    {
      id: "id_3",
      label: "id_3",
      token: "{{id_3}}",
    },
    ...(Array.isArray(values?.parameters?.properties)
      ? values.parameters.properties
      : []
    )
      .filter((property) => property?.key)
      .map((property) => ({
        id: property.id,
        label: property.key,
        token: `{{${property.key}}}`,
      })),
  ];

  const handlePlaceholderCopy = async (token) => {
    try {
      const copied = await copyToClipboard(token);
      if (copied) {
        setLastCopiedPlaceholder(token);
        toast.info(`Copied ${token}.`);
      } else {
        toast.error("Unable to copy placeholder. Please copy manually.");
      }
    } catch (error) {
      console.error("Failed to copy placeholder", error);
      toast.error("Failed to copy placeholder");
    }
  };

  const handleCurlImport = () => {
    if (!curlInput.trim()) {
      toast.error("Please enter a valid cURL command");
      return;
    }

    const parsed = parseCurlCommand(curlInput);
    if (!parsed || !parsed.url) {
      toast.error("Failed to parse cURL command. Ensure it has a valid URL.");
      return;
    }

    let formattedBody = parsed.body;
    if (parsed.body && parsed.body.trim()) {
      try {
        const parsedJson = JSON.parse(parsed.body);
        formattedBody = JSON.stringify(parsedJson, null, 2);
      } catch (error) {
        formattedBody = parsed.body;
      }
    }

    const newDetails = {
      ...values.executionLogic.details,
      url: parsed.url,
      method: parsed.method,
      headers: ensureIds(parsed.headers),
      body: formattedBody,
    };

    setValues({
      ...values,
      executionLogic: {
        ...values.executionLogic,
        details: newDetails,
      },
    });

    toast.success("Imported cURL configuration successfully!");
    setShowCurlInput(false);
    setCurlInput("");
  };

  const handleFormatJson = () => {
    try {
      const bodyText = details?.body || "";
      if (!bodyText.trim()) {
        toast.info("Body is empty");
        return;
      }

      const parsed = JSON.parse(bodyText);
      const formatted = JSON.stringify(parsed, null, 2);

      setValues({
        ...values,
        executionLogic: {
          ...executionLogic,
          details: { ...details, body: formatted },
        },
      });

      toast.success("JSON formatted successfully!");
    } catch (error) {
      toast.error("Invalid JSON format. Please check your syntax.");
    }
  };

  if (loading && isEditMode && !currentTool) {
    return (
      <div className="card border p-5 text-center">
        <span className="spinner-border text-primary"></span>
        <p className="mt-3 mb-0 small text-muted">Loading tool details...</p>
      </div>
    );
  }

  return (
    <div>
      <style>{editorFocusStyle}</style>
      <h3 className="mb-3 mt-2 d-flex align-items-center">
        <i className={`wb ${Icons("tools")} fs-8 me-2 mt-1 align-middle`}></i>
        Tool Setup & Configuration
      </h3>

      <form onSubmit={formik.handleSubmit} className="card-body p-4">
        <SectionCard title="1. Basic Information">
          <div className="mb-3">
            <label className="form-label fw-bold small text-muted">
              Tool Name
            </label>
            <input
              type="text"
              className={`form-control ${
                errors?.name && touched?.name ? "is-invalid" : ""
              }`}
              name="name"
              value={values?.name || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="e.g. Get Stock Price"
            />
            {errors?.name && (touched?.name || submitCount > 0) && (
              <div className="invalid-feedback d-block">{errors.name}</div>
            )}
          </div>

          <div>
            <label className="form-label fw-bold small text-muted">
              Description
            </label>
            <textarea
              className={`form-control ${
                errors?.description && touched?.description ? "is-invalid" : ""
              }`}
              name="description"
              rows="3"
              value={values?.description || ""}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              placeholder="Describe what this tool does."
              style={{
                resize: "none",
                minHeight: "70px",
                maxHeight: "150px",
                overflowY: "auto",
              }}
            />
            {errors?.description &&
              (touched?.description || submitCount > 0) && (
                <div className="invalid-feedback d-block">
                  {errors.description}
                </div>
              )}
          </div>
        </SectionCard>

        <SectionCard title="2. Input Parameters">
          <p className="text-muted small mb-3">
            Parameter names must use snake_case (lowercase letters, digits, and
            underscores only). These names populate the API configuration
            placeholders.
          </p>
          {Array.isArray(values?.parameters?.properties) &&
          values.parameters.properties.length === 0 ? (
            <p className="text-muted small text-center m-0">
              No inputs defined. The assistant will call this tool without
              arguments.
            </p>
          ) : (
            (Array.isArray(values?.parameters?.properties)
              ? values.parameters.properties
              : []
            ).map((property) => (
              <div
                key={property?.id}
                className="row g-2 align-items-center mb-3 border-bottom pb-3"
              >
                <div className="col-md-3">
                  <label className="small text-muted">Name</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="symbol"
                    value={property?.key || ""}
                    onChange={(event) =>
                      handleUpdateProperty(
                        property?.id,
                        "key",
                        event.target.value
                      )
                    }
                  />
                </div>
                <div className="col-md-2">
                  <label className="small text-muted">Type</label>
                  <select
                    className="form-select form-select-sm"
                    value={property?.type || "String"}
                    onChange={(event) =>
                      handleUpdateProperty(
                        property?.id,
                        "type",
                        event.target.value
                      )
                    }
                  >
                    <option value="String">String</option>
                    <option value="Number">Number</option>
                    <option value="Boolean">Boolean</option>
                    <option value="Array">Array</option>
                    <option value="Object">Object</option>
                  </select>
                </div>
                <div className="col-md-5">
                  <label className="small text-muted">Description</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="The stock ticker"
                    value={property?.description || ""}
                    onChange={(event) =>
                      handleUpdateProperty(
                        property?.id,
                        "description",
                        event.target.value
                      )
                    }
                  />
                </div>
                <div className="col-md-1 text-center">
                  <label className="small text-muted d-block">Req?</label>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={property?.isRequired ?? false}
                    onChange={(event) =>
                      handleUpdateProperty(
                        property?.id,
                        "isRequired",
                        event.target.checked
                      )
                    }
                  />
                </div>
                <div className="col-md-1 text-end">
                  <label className="d-block">&nbsp;</label>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    type="button"
                    onClick={() => handleRemoveProperty(property?.id)}
                  >
                    <i className={`wb ${Icons("delete")}`}></i>
                  </button>
                </div>
              </div>
            ))
          )}
          <div className="text-end mt-3">
            <button
              className="btn btn-sm btn-outline-secondary"
              type="button"
              onClick={handleAddProperty}
            >
              + Add Input
            </button>
          </div>
        </SectionCard>

        <SectionCard
          title="3. API Configuration"
          action={
            <button
              className="btn btn-sm btn-outline-secondary"
              type="button"
              onClick={() => setShowCurlInput(!showCurlInput)}
            >
              <i className={`wb ${Icons("download")} me-1`}></i>
              Import cURL
            </button>
          }
        >
          {showCurlInput && (
            <div className="alert border small text-muted">
              <label className="form-label fw-bold small text-muted">
                Paste cURL Command
              </label>
              <textarea
                className="form-control font-monospace mb-2"
                rows="4"
                placeholder="curl -X POST https://api.example.com/data -H 'Content-Type: application/json' -d '...'"
                value={curlInput}
                onChange={(e) => setCurlInput(e.target.value)}
                style={{ fontSize: "0.8rem" }}
              />
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowCurlInput(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleCurlImport}
                >
                  Import
                </button>
              </div>
            </div>
          )}
          <div className="alert border small text-muted">
            <i className={`wb ${Icons("info")} me-2`}></i>
            Use{" "}
            <span className="badge bg-secondary font-monospace">{`{{variable_name}}`}</span>{" "}
            to inject input values inside the URL, headers, or body.
          </div>
          {parameterPlaceholders.length > 0 && (
            <div className="mb-3">
              <label className="form-label fw-bold small text-muted">
                Available placeholders
              </label>
              <div className="d-flex flex-wrap gap-2">
                {parameterPlaceholders.map(({ id, token }) => (
                  <button
                    key={id}
                    type="button"
                    className={`btn btn-sm border ${
                      token === lastCopiedPlaceholder
                        ? "btn-outline-success"
                        : "btn-outline-secondary"
                    } font-monospace`}
                    style={{
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#f8f9fa";
                      e.currentTarget.style.borderColor = "#0d6efd";
                      e.currentTarget.style.color = "#0d6efd";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "";
                      e.currentTarget.style.borderColor = "";
                      e.currentTarget.style.color = "";
                    }}
                    onClick={() => handlePlaceholderCopy(token)}
                  >
                    {token}
                    <i className={`wb ${Icons("copy")} me-1`}></i>
                  </button>
                ))}
              </div>
              <div className="form-text small mt-1">
                Click a placeholder to copy it before pasting into the URL,
                headers, or body fields.
              </div>
            </div>
          )}
          <div className="mb-3">
            <label className="form-label fw-bold small text-muted">
              API URL
            </label>
            <div className="input-group">
              <select
                className="form-select flex-grow-0 w-auto fw-bold"
                name="executionLogic.details.method"
                value={details?.method || "POST"}
                onChange={(event) =>
                  setValues({
                    ...values,
                    executionLogic: {
                      ...executionLogic,
                      details: { ...details, method: event.target.value },
                    },
                  })
                }
              >
                <option value="GET">GET</option>
                <option value="POST">POST</option>
                <option value="PUT">PUT</option>
                <option value="PATCH">PATCH</option>
                <option value="DELETE">DELETE</option>
              </select>
              <input
                type="text"
                className={`form-control ${
                  errors?.executionLogic?.details?.url ? "is-invalid" : ""
                }`}
                placeholder="https://api.example.com/v1/resource"
                value={details?.url || ""}
                onChange={(event) =>
                  setValues({
                    ...values,
                    executionLogic: {
                      ...executionLogic,
                      details: { ...details, url: event.target.value },
                    },
                  })
                }
              />
            </div>
            {errors?.executionLogic?.details?.url && (
              <div className="invalid-feedback d-block">
                {errors.executionLogic.details.url}
              </div>
            )}
          </div>

          <ul className="nav nav-tabs mb-3">
            {["params", "headers", "body"].map((tab) => (
              <li className="nav-item" key={tab}>
                <button
                  className={`nav-link ${
                    activeTab === tab ? "active fw-bold" : ""
                  }`}
                  type="button"
                  onClick={() => setValues({ ...values, _activeTab: tab })}
                >
                  {tab === "params"
                    ? "Query Params"
                    : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              </li>
            ))}
          </ul>

          {activeTab === "params" && (
            <KeyValueEditor
              items={
                Array.isArray(details?.queryParams) ? details.queryParams : []
              }
              onAdd={() => handleAddKeyValue("executionLogic")}
              onChange={(id, field, value) =>
                handleUpdateKeyValue("executionLogic", id, field, value)
              }
              onRemove={(id) => handleRemoveKeyValue("executionLogic", id)}
              keyPlaceholder="Key"
              valuePlaceholder="Value"
              addLabel="+ Add Param"
            />
          )}

          {activeTab === "headers" && (
            <KeyValueEditor
              items={Array.isArray(details?.headers) ? details.headers : []}
              onAdd={() => handleAddKeyValue("executionLogic")}
              onChange={(id, field, value) =>
                handleUpdateKeyValue("executionLogic", id, field, value)
              }
              onRemove={(id) => handleRemoveKeyValue("executionLogic", id)}
              keyPlaceholder="Key (e.g. Authorization)"
              valuePlaceholder="Value"
              addLabel="+ Add Header"
            />
          )}

          {activeTab === "body" && (
            <div>
              {details?.method === "GET" ? (
                <div className="alert alert-info mb-0" role="alert">
                  <i className={`wb ${Icons("info")} me-2`}></i>
                  GET requests typically do not send a body.
                </div>
              ) : (
                <>
                  <div className="mb-2 d-flex justify-content-between align-items-center">
                    <label className="form-label fw-bold small text-muted mb-0">
                      Request Body (JSON)
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={handleFormatJson}
                    >
                      <i className={`wb ${Icons("code")} me-1`}></i>
                      Format JSON
                    </button>
                  </div>
                  <textarea
                    className="form-control font-monospace"
                    rows="10"
                    value={details?.body}
                    onChange={(event) =>
                      setValues({
                        ...values,
                        executionLogic: {
                          ...executionLogic,
                          details: { ...details, body: event.target.value },
                        },
                      })
                    }
                    style={{
                      backgroundColor: "#2d3748",
                      color: "#e2e8f0",
                      fontFamily: "'Courier New', monospace",
                      fontSize: "0.75rem",
                      border: "1px solid #4a5568",
                      lineHeight: "1.4",
                    }}
                    spellCheck="false"
                  />
                  <div className="form-text small mt-2">
                    <i className={`wb ${Icons("info")} me-1`}></i>
                    Enter valid JSON format. Ensure all property names and
                    string values are properly quoted.
                  </div>
                </>
              )}
            </div>
          )}
        </SectionCard>

        <div className="d-flex justify-content-between">
          {toolUuidRef?.current && (
            <button
              className="btn btn-outline-danger"
              type="button"
              onClick={handleDelete}
            >
              <i className={`wb ${Icons("delete")} fs-8 me-2 align-middle`}></i>
              Delete
            </button>
          )}

          <button
            className="btn btn-primary"
            type="submit"
            disabled={isSubmitting}
          >
            <i className={`wb ${Icons("save")} fs-8 me-2 align-middle`}></i>
            {isSubmitting
              ? toolUuidRef?.current
                ? "Updating..."
                : "Saving..."
              : toolUuidRef?.current
              ? "Update Tool"
              : "Save Tool"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ToolEditor;
